import { useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";

import { transportService, type Allocation, type TransportIncident, type Trip } from "../../services/transportService";
import { apiErrorMessage } from "../../utils/apiError";
import { nowLocalInput, todayIso } from "../../utils/dates";
import { Field, FormSection } from "../FormLayout";
import { FormModal } from "../FormModal";
import { SearchPick } from "../library/Pickers";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { ActionPanel, DetailsModal } from "../register/DetailsModal";
import { Notice, type NoticeState } from "../register/RecordParts";
import { AssistedText, errorLine } from "./TransportParts";
import { INCIDENT_TYPES, SEVERITIES, clock, day, incidentConfig, incidentStatus, labelFrom, severityDot } from "./transportLogic";

/** What happened on a trip, what was done straight away, and who was involved. */
export function IncidentForm({ trips, allocations, fixedTrip, onClose, onDone }: {
  /** Trips that have left, newest first. */
  trips: Trip[];
  allocations: Allocation[];
  fixedTrip?: Trip;
  onClose: () => void;
  onDone: (incident: TransportIncident) => void;
}) {
  const [tripId, setTripId] = useState(fixedTrip ? String(fixedTrip.id) : "");
  const [title, setTitle] = useState("");
  const [kind, setKind] = useState("breakdown");
  const [severity, setSeverity] = useState("minor");
  const [location, setLocation] = useState("");
  const [description, setDescription] = useState("");
  const [action, setAction] = useState("");
  const [involved, setInvolved] = useState<number[]>([]);
  const trip = fixedTrip ?? trips.find((row) => String(row.id) === tripId);
  const riders = trip ? allocations.filter((row) => row.route === trip.route && row.status === "active") : [];
  const save = useMutation({ mutationFn: transportService.reportIncident, onSuccess: onDone });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!trip) return;
    const data = new FormData(event.currentTarget);
    save.mutate({
      trip: trip.id,
      incident_type: kind,
      severity,
      title: title.trim(),
      incident_date: trip.trip_date,
      incident_time: String(data.get("time")),
      location: location.trim(),
      description: description.trim(),
      immediate_action: action.trim(),
      students_involved: involved,
      external_people_involved: String(data.get("external") ?? "").trim() || null,
      emergency_services_called: data.get("emergency") === "on",
      police_report_filed: data.get("police") === "on",
      insurance_claim_filed: data.get("insurance") === "on",
      investigation_required: data.get("investigate") === "on",
    });
  };
  const context = (field: string) => ({ key: "transport.note" as const, params: { kind: "incident", field, trip: trip?.id, title, incident_type: kind, severity, location } });
  const nowTime = nowLocalInput().slice(11, 16);

  return (
    <FormModal
      id="report-incident"
      title="Report an incident"
      busy={save.isPending}
      error={save.isError ? apiErrorMessage(save.error, "The incident could not be reported.") : undefined}
      saveLabel="Report it"
      note="The transport manager follows it up."
      onClose={onClose}
      onSubmit={submit}
    >
      <FormSection title="Which trip" divided={false}>
        {fixedTrip ? (
          <p className="text-sm text-gray-700 sm:col-span-2">
            {fixedTrip.route_name} · {day(fixedTrip.trip_date)} · {fixedTrip.vehicle_name} · {fixedTrip.driver_name}
          </p>
        ) : (
          <Field label="Trip" as="div" className="sm:col-span-2" hint="Only trips that have left are listed.">
            <SearchPick
              items={trips}
              value={tripId}
              onChange={setTripId}
              label={(row) => `${day(row.trip_date)} · ${row.route_name}`}
              detail={(row) => `${row.vehicle_name} · ${row.driver_name}`}
              placeholder="Search by route, bus or driver"
              empty="No trips have left yet"
              required
            />
          </Field>
        )}
      </FormSection>

      <FormSection title="What happened">
        <Field label="Title" className="sm:col-span-2" hint="e.g. Flat tyre near Kira junction.">
          <input required value={title} onChange={(event) => setTitle(event.target.value)} maxLength={200} className="input mt-1" />
        </Field>
        <Field label="Kind">
          <select value={kind} onChange={(event) => setKind(event.target.value)} className="input mt-1">
            {INCIDENT_TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </Field>
        <Field label="How serious">
          <select value={severity} onChange={(event) => setSeverity(event.target.value)} className="input mt-1">
            {SEVERITIES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </Field>
        <Field label="Time" hint={trip ? `On ${day(trip.trip_date)}, the trip's date.` : undefined}>
          <input required type="time" name="time" defaultValue={trip?.trip_date === todayIso() ? nowTime : clock(trip?.scheduled_departure)} className="input mt-1" />
        </Field>
        <Field label="Where">
          <input required value={location} onChange={(event) => setLocation(event.target.value)} maxLength={200} className="input mt-1" />
        </Field>
        <Field label="Describe it" as="div" className="sm:col-span-2">
          <AssistedText
            name="description"
            value={description}
            onChange={setDescription}
            context={context("description")}
            placeholder="What happened, in order."
            draftHint="Jot the facts first; it tidies them up and never adds any."
          />
        </Field>
        <Field label="What was done straight away" as="div" className="sm:col-span-2">
          <AssistedText
            name="immediate_action"
            value={action}
            onChange={setAction}
            rows={2}
            context={context("immediate_action")}
            placeholder="e.g. Learners moved to Bus Two; parents called."
            draftHint="Jot what was done; it writes it up without adding anything."
          />
        </Field>
      </FormSection>

      <FormSection title="Who was involved">
        {riders.length > 0 ? (
          <fieldset className="sm:col-span-2">
            <legend className="text-sm font-medium text-gray-700">Learners on this route <span className="font-normal text-gray-400">(optional)</span></legend>
            <div className="mt-1 grid max-h-40 gap-1 overflow-y-auto rounded-md border border-gray-200 p-2 sm:grid-cols-2">
              {riders.map((rider) => (
                <label key={rider.id} className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={involved.includes(rider.student)}
                    onChange={(event) => setInvolved((current) => (event.target.checked ? [...current, rider.student] : current.filter((id) => id !== rider.student)))}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  {rider.student_name}
                </label>
              ))}
            </div>
          </fieldset>
        ) : null}
        <Field label="Other people" optional className="sm:col-span-2" hint="e.g. the other driver, a boda-boda rider, a passer-by who helped.">
          <input name="external" className="input mt-1" />
        </Field>
        <div className="grid gap-2 text-sm text-gray-700 sm:col-span-2 sm:grid-cols-2">
          {[
            ["emergency", "Police, ambulance or fire were called"],
            ["police", "A police report was made"],
            ["insurance", "An insurance claim was made"],
            ["investigate", "It needs investigating before it is closed"],
          ].map(([name, label]) => (
            <label key={name} className="flex items-center gap-2">
              <input type="checkbox" name={name} className="h-4 w-4 rounded border-gray-300" /> {label}
            </label>
          ))}
        </div>
      </FormSection>
    </FormModal>
  );
}

/** Breakdowns, accidents and anything else that happened on a trip, followed up to the end. */
export function IncidentsPanel({ incidents, trips, allocations, isLoading, isError, onRetry, canManage, onChanged , initialView}: {
  /** A register view to open on, e.g. "papers" from the dashboard. */
  initialView?: string;
  incidents?: TransportIncident[];
  trips: Trip[];
  allocations: Allocation[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  canManage: boolean;
  onChanged: () => void;
}) {
  const today = todayIso();
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [reporting, setReporting] = useState(false);
  const [corrective, setCorrective] = useState("");
  const [prevention, setPrevention] = useState("");
  const [resolution, setResolution] = useState("");
  const done = (text: string) => {
    setNotice({ tone: "success", text });
    onChanged();
  };
  const investigate = useMutation({ mutationFn: transportService.investigateIncident, onSuccess: (row) => done(`The investigation into “${row.title}” is recorded.`) });
  const resolve = useMutation({
    mutationFn: transportService.resolveIncident,
    onSuccess: (row) => {
      setViewing(null);
      done(`“${row.title}” is resolved.`);
    },
  });
  const departed = [...trips].filter((row) => row.trip_date <= today && (row.status === "in_progress" || row.status === "completed")).sort((a, b) => b.trip_date.localeCompare(a.trip_date));

  const columns: RegisterColumn<TransportIncident>[] = [
    { header: "When", cell: (row) => `${day(row.incident_date)} ${clock(row.incident_time)}`, exportValue: (row) => `${day(row.incident_date)} ${clock(row.incident_time)}`, className: "whitespace-nowrap tabular-nums text-gray-700" },
    { header: "Incident", cell: (row) => <span className="font-medium text-gray-900">{row.title}</span>, exportValue: (row) => row.title, className: "min-w-[12rem] whitespace-normal" },
    { header: "Kind", cell: (row) => labelFrom(INCIDENT_TYPES, row.incident_type), exportValue: (row) => labelFrom(INCIDENT_TYPES, row.incident_type), className: "text-gray-700" },
    { header: "How serious", cell: (row) => <StatusDot label={labelFrom(SEVERITIES, row.severity)} dot={severityDot(row.severity)} />, exportValue: (row) => labelFrom(SEVERITIES, row.severity) },
    { header: "Route", cell: (row) => row.route_name, exportValue: (row) => row.route_name, className: "text-gray-700" },
    { header: "Vehicle", cell: (row) => row.vehicle_name, exportValue: (row) => row.vehicle_name, className: "text-gray-700" },
    { header: "Where", cell: (row) => row.location, exportValue: (row) => row.location, exportOnly: true },
    { header: "What happened", cell: (row) => row.description, exportValue: (row) => row.description, exportOnly: true },
    { header: "Status", cell: (row) => <StatusDot {...incidentStatus(row)} />, exportValue: (row) => incidentStatus(row).label },
  ];

  const incident = viewing !== null ? incidents?.find((row) => row.id === viewing) : undefined;
  const canResolve = incident && (!incident.investigation_required || incident.investigation_completed);

  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        key={initialView}
        initialState={initialView ? { view: initialView } : undefined}
        title="Incidents"
        description={canManage ? "Open an incident to record the investigation and close it." : "Incidents on your trips and how they were followed up."}
        actions={
          <button type="button" disabled={!departed.length} onClick={() => setReporting(true)} className="btn-primary h-8 min-h-8 px-3 text-xs disabled:opacity-50">
            <AlertTriangle className="mr-1.5 h-3.5 w-3.5" /> Report an incident
          </button>
        }
        rows={incidents}
        config={incidentConfig}
        columns={columns}
        rowKey={(row) => row.id}
        onOpen={(row) => {
          investigate.reset();
          resolve.reset();
          setCorrective("");
          setPrevention("");
          setResolution("");
          setViewing(row.id);
        }}
        openLabel={(row) => (canManage && !row.is_resolved ? "Follow up" : "Open")}
        noun={["incident", "incidents"]}
        exportName={{ file: "transport-incidents", title: "Transport Incidents" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText="No incidents reported."
      />

      {incident && (
        <DetailsModal
          size="3xl"
          title={incident.title}
          headline={{ label: labelFrom(INCIDENT_TYPES, incident.incident_type), value: `${day(incident.incident_date)} ${clock(incident.incident_time)}`, note: `${incident.location} · ${incident.route_name} · ${incident.vehicle_name}` }}
          status={incidentStatus(incident)}
          facts={[
            ["How serious", <StatusDot key="severity" label={labelFrom(SEVERITIES, incident.severity)} dot={severityDot(incident.severity)} />],
            ["Reported by", incident.reported_by_name],
            ["Learners involved", incident.students_involved_names.join(", ") || "None named"],
            ["Other people", incident.external_people_involved ?? undefined],
            ["Emergency services", incident.emergency_services_called ? "Called" : "Not called"],
            ["Police report", incident.police_report_filed ? "Made" : "None"],
            ["Insurance claim", incident.insurance_claim_filed ? "Made" : "None"],
            ["Investigation", incident.investigation_completed ? `Done ${day(incident.investigation_completed_date)} by ${incident.investigated_by_name}` : incident.investigation_required ? "Needed" : "Not needed"],
            ["Resolved", incident.is_resolved ? `${day(incident.resolution_date)} by ${incident.resolved_by_name}` : undefined],
          ]}
          sections={[
            { title: "What happened", body: incident.description },
            { title: "Done straight away", body: incident.immediate_action },
            { title: "Corrective actions", body: incident.corrective_actions },
            { title: "To stop it happening again", body: incident.prevention_measures },
            { title: "How it was resolved", body: incident.resolution_notes },
            { title: "Notes", body: incident.notes },
          ]}
          onClose={() => setViewing(null)}
        >
          {canManage && !incident.is_resolved && !incident.investigation_completed && (
            <ActionPanel title="Record the investigation" description={incident.investigation_required ? "Needed before the incident can be closed." : "Optional for this incident."}>
              <Field label="What was put right" as="div">
                <AssistedText name="corrective" value={corrective} onChange={setCorrective} rows={2} context={{ key: "transport.note", params: { kind: "incident", field: "corrective_actions", incident: incident.id } }} draftHint="Jot what was done; it writes it up without adding anything." />
              </Field>
              <Field label="What will stop it happening again" as="div">
                <AssistedText name="prevention" value={prevention} onChange={setPrevention} rows={2} context={{ key: "transport.note", params: { kind: "incident", field: "prevention_measures", incident: incident.id } }} draftHint="Jot the changes; it writes them up without adding any." />
              </Field>
              <div className="flex justify-end">
                <button
                  type="button"
                  disabled={!corrective.trim() || !prevention.trim() || investigate.isPending}
                  onClick={() => investigate.mutate({ id: incident.id, corrective_actions: corrective.trim(), prevention_measures: prevention.trim() })}
                  className="btn btn-outline"
                >
                  Save the investigation
                </button>
              </div>
              {investigate.isError && errorLine(investigate.error, "The investigation could not be saved.")}
            </ActionPanel>
          )}
          {canManage && !incident.is_resolved && canResolve && (
            <ActionPanel title="Close the incident" description="Say how it was settled.">
              <Field label="How it was resolved" as="div">
                <AssistedText name="resolution" value={resolution} onChange={setResolution} rows={2} context={{ key: "transport.note", params: { kind: "incident", field: "resolution_notes", incident: incident.id } }} draftHint="Jot how it ended; it writes it up without adding anything." />
              </Field>
              <div className="flex justify-end">
                <button type="button" disabled={!resolution.trim() || resolve.isPending} onClick={() => resolve.mutate({ id: incident.id, resolution_notes: resolution.trim() })} className="btn btn-primary">
                  Close the incident
                </button>
              </div>
              {resolve.isError && errorLine(resolve.error, "The incident could not be closed.")}
            </ActionPanel>
          )}
        </DetailsModal>
      )}

      {reporting && (
        <IncidentForm
          trips={departed}
          allocations={allocations}
          onClose={() => setReporting(false)}
          onDone={(saved) => {
            setReporting(false);
            done(`Incident “${saved.title}” reported.`);
            setViewing(saved.id);
          }}
        />
      )}
    </div>
  );
}
